import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, Grid, Menu, MenuItem, Typography } from '@material-ui/core';
import { InfoOutlined, SearchOutlined } from '@material-ui/icons';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Alert, AlertTitle, IconButton, TextField, Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';

import { fbmApi as api } from 'api/fbm';
import { FBMOrdersV2Api } from 'api/fbmOrdersV2';
import { shippingApi } from 'api/shipping';
import { useGetTableData } from 'components/fbm/hooks/useGetTableData';
import { UserContext } from 'contexts/UserContext';
import { FacilityPicker, Notifications } from 'shared';
import { downloadCSV } from 'shared/components/csvDownload';
import ExpandableDataGrid from 'shared/components/expandableDataGrid/expandableDataGrid';
import dayjs from 'utils/dayjsConfig';

import { CancelFBMOrderModal } from './CancelFBMOrderModal';
import { CancelFBMShipmentModal } from './CancelFBMShipmentModal';
import { ChangeFBMOrderCarrierServiceModal } from './ChangeFBMOrderCarrierServiceModal';
import { ChangeFBMOrderLineHaulModal } from './ChangeFBMOrderLineHaulModal';
import { ChangeFBMShipmentCarrierServiceModal } from './ChangeFBMShipmentCarrierServiceModal';
import { ChangeFBMShipmentLineHaulModal } from './ChangeFBMShipmentLineHaulModal';
import { ResetFBMOrderStatusModal } from './ResetFBMOrderStatusModal';
import { ResetFBMShipmentStatusModal } from './ResetFBMShipmentStatusModal';

function ViewAllOrdersV2() {
    const [facilityID, setFacilityID] = useState(null);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [anchorEl, setAnchorEl] = useState(undefined);
    const [menuRowId, setMenuRowId] = useState(undefined);
    const [selectedRow, setSelectedRow] = useState(undefined);
    const [shipmentModal, setShipmentModal] = useState({
        cancelModalIsOpen: false,
        changeCarrierServiceModalIsOpen: false,
        resetStatusModalIsOpen: false,
        changeLineHaulModalIsOpen: false,
        shipment: undefined,
    });
    const [orderModal, setOrderModal] = useState({
        cancelOrderModalIsOpen: false,
        changeCarrierServiceModalIsOpen: false,
        resetOrderModalIsOpen: false,
        changeLineHaulModalIsOpen: false,
        order: undefined,
        shipments: [],
    });
    const [bulkFailedOrders, setBulkFailedOrders] = useState([]);
    const [search, setSearch] = useState({
        term: '',
        q: undefined,
    });

    const { fbmAccount, user } = useContext(UserContext);
    const history = useHistory();

    const fbmOrdersV2Api = new FBMOrdersV2Api();

    const fetchOrders = useCallback(
        () => fbmOrdersV2Api.getOrders(fbmAccount.fbmAccountName, facilityID, search.q),
        [fbmAccount.fbmAccountName, facilityID, search.q],
    );

    const fetchExportOrderRows = useCallback(
        () => fbmOrdersV2Api.getExportOrderRows(fbmAccount.fbmAccountName, facilityID),
        [fbmAccount.fbmAccountName, facilityID],
    );

    useEffect(() => {
        if (!!user.id && !user.fbm) {
            setFacilityID(user.facilityID);
        }
    }, [user]);

    const {
        data: orderData,
        error: orderError,
        isLoading: ordersLoading,
        refetch: refetchOrders,
    } = useGetTableData(fetchOrders);

    const fetchShipments = useCallback(
        () => fbmOrdersV2Api.getShipments(selectedRow?.fbmOrderNum, fbmAccount.fbmAccountName),
        [selectedRow?.fbmOrderNum, fbmAccount.fbmAccountName],
    );

    const { data: shipmentsData, error: shipmentsError, isLoading: shipmentsLoading } = useGetTableData(fetchShipments);

    const [selectedRowsIds, setSelectedRowsIds] = useState([]);

    // Handle menu opening
    const handleMenuOpen = (event, rowId) => {
        // Stop propagation to prevent row expansion
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
        setSelectedRow(undefined);
    };

    const handleShipmentMenuOpen = (event, rowId) => {
        // Stop propagation to prevent row expansion
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
    };

    const handleMenuClose = (event) => {
        if (event) {
            event.stopPropagation();
        }
        setAnchorEl(null);
        setMenuRowId(null);
    };

    const handleGoToOrderDeepDive = (event, row) => {
        event.stopPropagation();
        handleMenuClose();
        history.push(`/fbm/orders?tab=1&fbmOrderNum=${encodeURIComponent(row.fbmOrderNum)}`);
    };

    const handleExportAll = async () => {
        const response = await fetchExportOrderRows();
        let headerContent = '';
        let keys = [];

        if (!response.status) {
            setNotification({ text: response.message, level: 'error' });
            return;
        }

        if (response.data.rows.length > 0) {
            keys = Object.keys(response.data.rows[0]);
            headerContent = keys.join(',');
        }
        let csvContent = `${headerContent}\r\n`;

        for (const row of response.data.rows) {
            const rowContent = keys.map((key) => row[key]).join(',');
            csvContent += `${rowContent}\r\n`;
        }

        const today = dayjs();
        const filename = `${fbmAccount.fbmAccountName}_ORDERS${today.format('_YYYY_MM_DD_HH_mm')}.csv`;

        downloadCSV(csvContent, filename);
    };

    const columns = [
        { field: 'fbmOrderNum', headerName: 'FBM Order Number', flex: 1 },
        { field: 'orderId', headerName: 'Order Id', flex: 1 },
        { field: 'shipmentStatus', headerName: 'Status', flex: 1 },
        { field: 'expectedPackDate', headerName: 'Expected Pack Date', flex: 1 },
        { field: 'service', headerName: 'Service', flex: 1 },
        { field: 'boxes', headerName: 'Box Count', flex: 0.5 },
        { field: 'boxName', headerName: 'Box Name', flex: 1 },
        {
            field: 'createdAt',
            headerName: `Created Time (${dayjs().tz(dayjs.tz.guess()).format('z')})`,
            flex: 1,
            valueFormatter: (params) => dayjs(params).local().format('MM/DD/YYYY h:mm a'),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.7,
            renderCell: (params) => (
                <>
                    <Button
                        aria-controls={`row-menu-${params.id}`}
                        aria-haspopup="true"
                        onClick={(e) => handleMenuOpen(e, params.id)}
                        size="small"
                        startIcon={<MoreVertIcon />}
                    >
                        Actions
                    </Button>

                    <Menu
                        id={`row-menu-${params.id}`}
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl) && menuRowId === params.id}
                        onClose={handleMenuClose}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MenuItem onClick={(e) => handleGoToOrderDeepDive(e, params.row)}>Go To Deep Dive</MenuItem>
                        <MenuItem onClick={(e) => handleOpenCancelOrderModal(params.row)}>Cancel Order</MenuItem>
                        <MenuItem onClick={(e) => handleOpenChangeOrderCarrierServiceModal(params.row)}>
                            Change Carrier Service
                        </MenuItem>
                        <MenuItem onClick={(e) => handleOpenResetOrderModal(params.row)}>Reset Order Status</MenuItem>
                        <MenuItem onClick={(e) => handleOpenChangeOrderLineHaulModal(params.row)}>
                            Change Line Haul
                        </MenuItem>
                    </Menu>
                </>
            ),
        },
    ];

    const childColumns = [
        { field: 'shipmentId', headerName: 'Shipment ID', flex: 1 },
        { field: 'status', headerName: 'Status', flex: 1 },
        { field: 'carrier', headerName: 'Carrier', flex: 1 },
        { field: 'hub', headerName: 'Hub', flex: 1 },
        { field: 'trackingUrl', headerName: 'Tracking Url', flex: 2 },
        { field: 'arrivalDay', headerName: 'Ship Day', flex: 1 },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.7,
            renderCell: (params) => (
                <>
                    <Button
                        aria-controls={`row-menu-${params.id}`}
                        aria-haspopup="true"
                        onClick={(e) => handleShipmentMenuOpen(e, params.id)}
                        size="small"
                        startIcon={<MoreVertIcon />}
                    >
                        Actions
                    </Button>

                    <Menu
                        id={`row-menu-${params.id}`}
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl) && menuRowId === params.id}
                        onClose={handleMenuClose}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MenuItem onClick={(e) => handleOpenCancelShipmentModal(params.row)}>Cancel Shipment</MenuItem>
                        <MenuItem onClick={(e) => handleOpenChangeShipmentCarrierServiceModal(params.row)}>
                            Change Carrier Service
                        </MenuItem>
                        <MenuItem onClick={(e) => handleOpenResetShipmentLabelModal(params.row)}>Reset Label</MenuItem>
                        <MenuItem onClick={(e) => handleOpenChangeLineHaulModal(params.row)}>Change Line Haul</MenuItem>
                        <MenuItem onClick={(e) => handleDownloadLPN(params.row)}>Download LPN</MenuItem>
                    </Menu>
                </>
            ),
        },
    ];

    const [isDownloadingIngest, setIsDownloadingIngest] = useState(false);
    const handleDownloadIngestResults = useCallback(async () => {
        setIsDownloadingIngest(true);
        try {
            await api.downloadIngestResults(fbmAccount.fbmAccountName);
        } catch (e) {
            setNotification({ text: 'Failed to download ingest results. Please try again.', level: 'error' });
        } finally {
            setIsDownloadingIngest(false);
        }
    }, [fbmAccount.fbmAccountName]);

    const bulkActions = [
        {
            componentJSX() {
                return (
                    <Button
                        variant="outlined"
                        color="secondary"
                        style={{ marginRight: 10 }}
                        onClick={handleCancelSelected}
                    >
                        Cancel Selected
                    </Button>
                );
            },
        },
        {
            componentJSX() {
                return (
                    <Button
                        variant="outlined"
                        color="secondary"
                        style={{ marginRight: 10 }}
                        onClick={handleResetLabelSelected}
                    >
                        Reset Label Selected
                    </Button>
                );
            },
        },
        {
            componentJSX() {
                return (
                    <Button variant="outlined" color="secondary" style={{ marginRight: 10 }} onClick={handleExportAll}>
                        Export All
                    </Button>
                );
            },
        },
        {
            componentJSX() {
                return (
                    <Button
                        variant="outlined"
                        color="secondary"
                        style={{ marginRight: 10 }}
                        onClick={handleDownloadIngestResults}
                        disabled={isDownloadingIngest}
                    >
                        {isDownloadingIngest ? <CircularProgress size={18} /> : 'Download Ingest Results'}
                    </Button>
                );
            },
        },
    ];

    const handleDownloadLPN = useCallback(async (row) => {
        await shippingApi.getShipmentLabelPDF(row.shipmentId);
    }, []);

    const handleResetLabelSelected = useCallback(async () => {
        setBulkFailedOrders([]);
        const response = await fbmOrdersV2Api.bulkResetOrderStatus(
            selectedRowsIds,
            fbmAccount.fbmAccountName,
            'Bulk Reset Label',
        );
        if (response.status) {
            setNotification({ text: response.msg, level: 'success' });
        } else {
            setBulkFailedOrders(response.data.failures);
            setNotification({ text: response.msg, level: 'error' });
        }
        refetchOrders();
    }, [selectedRowsIds]);

    const handleCancelSelected = useCallback(async () => {
        setBulkFailedOrders([]);
        const response = await fbmOrdersV2Api.bulkCancelOrders(
            selectedRowsIds,
            fbmAccount.fbmAccountName,
            'Bulk Cancel',
        );
        if (response.status) {
            setNotification({ text: response.msg, level: 'success' });
        } else {
            setBulkFailedOrders(response?.data?.failures || []);
            setNotification({ text: response.msg, level: 'error' });
        }
        refetchOrders();
    }, [selectedRowsIds]);

    // Used for the rows that are selected via checkboxes
    const handleSelectedRowsChange = (selectedRows) => {
        setSelectedRowsIds(selectedRows);
        setSelectedRow(undefined);
    };

    const handleSearch = () => {
        setSearch({
            ...search,
            q: encodeURIComponent(search.term),
        });
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleOpenCancelShipmentModal = (row) => {
        handleMenuClose();
        setShipmentModal((prevState) => ({
            ...prevState,
            cancelModalIsOpen: true,
            shipment: row,
        }));
        handleMenuClose();
    };

    const handleCancelShipmentModalClose = () => {
        setShipmentModal((prevState) => ({
            ...prevState,
            cancelModalIsOpen: false,
            shipment: undefined,
        }));
    };

    const handleCancelShipmentModalSuccess = (msg) => {
        handleCancelShipmentModalClose();
        setNotification({ text: msg, level: 'success' });
        refetchOrders();
        setSelectedRow(undefined);
    };

    const handleCancelShipmentModalError = (msg) => {
        setNotification({ text: msg, level: 'error' });
    };

    const handleChangeShipmentCarrierServiceModalClose = () => {
        setShipmentModal((prevState) => ({
            ...prevState,
            changeCarrierServiceModalIsOpen: false,
            shipment: undefined,
        }));
    };

    const handleChangeOrderCarrierServiceModalClose = () => {
        setOrderModal((prevState) => ({
            ...prevState,
            changeCarrierServiceModalIsOpen: false,
            order: undefined,
        }));
    };

    const handleChangeShipmentCarrierServiceModalSuccess = (msg) => {
        handleChangeShipmentCarrierServiceModalClose();
        setNotification({ text: msg, level: 'success' });
        refetchOrders();
        setSelectedRow(undefined);
    };

    const handleChangeShipmentCarrierServiceModalError = (msg) => {
        setNotification({ text: msg, level: 'error' });
    };

    const handleOpenChangeShipmentCarrierServiceModal = (row) => {
        handleMenuClose();
        setShipmentModal((prevState) => ({
            ...prevState,
            changeCarrierServiceModalIsOpen: true,
            shipment: row,
        }));
        handleMenuClose();
    };

    const handleOpenChangeOrderCarrierServiceModal = (row) => {
        handleMenuClose();
        setOrderModal((prevState) => ({
            ...prevState,
            changeCarrierServiceModalIsOpen: true,
            order: row,
        }));
    };

    const handleOpenResetShipmentLabelModal = (row) => {
        handleMenuClose();
        setShipmentModal((prevState) => ({
            ...prevState,
            resetStatusModalIsOpen: true,
            shipment: row,
        }));
        handleMenuClose();
    };

    const handleResetStatusModalClose = () => {
        setShipmentModal((prevState) => ({
            ...prevState,
            resetStatusModalIsOpen: false,
            shipment: undefined,
        }));
    };

    const handleResetStatusModalSuccess = (msg) => {
        handleResetStatusModalClose();
        setNotification({ text: msg, level: 'success' });
        refetchOrders();
        setSelectedRow(undefined);
    };

    const handleResetStatusModalError = (msg) => {
        setNotification({ text: msg, level: 'error' });
    };

    const handleColumnHeaderClick = useCallback((params) => {
        setSelectedRow(undefined);
    }, []);

    const handleSortModelChange = useCallback((newSortModel) => {
        setSelectedRow(undefined);
    }, []);

    const handleFilterModelChange = useCallback(() => {
        setSelectedRow(undefined);
    }, []);

    const handleOpenCancelOrderModal = (row) => {
        handleMenuClose();
        setOrderModal((prevState) => ({
            ...prevState,
            cancelOrderModalIsOpen: true,
            order: row,
        }));
        handleMenuClose();
    };

    const handleCancelOrderModalClose = () => {
        setOrderModal((prevState) => ({
            ...prevState,
            cancelOrderModalIsOpen: false,
            order: undefined,
        }));
    };

    const handleCancelOrderModalSuccess = (msg) => {
        handleCancelOrderModalClose();
        setNotification({ text: msg, level: 'success' });
        refetchOrders();
    };

    const handleCancelOrderModalError = (msg) => {
        setNotification({ text: msg, level: 'error' });
    };

    const handleClearFailures = () => {
        setBulkFailedOrders([]);
    };
    const handleOpenChangeLineHaulModal = (row) => {
        handleMenuClose();
        setShipmentModal((prevState) => ({
            ...prevState,
            changeLineHaulModalIsOpen: true,
            shipment: row,
        }));
    };

    const handleChangeLineHaulModalClose = () => {
        setShipmentModal((prevState) => ({
            ...prevState,
            changeLineHaulModalIsOpen: false,
            shipment: undefined,
        }));
    };

    const handleChangeLineHaulModalSuccess = (msg) => {
        handleChangeLineHaulModalClose();
        setNotification({ text: msg, level: 'success' });
        refetchOrders();
        setSelectedRow(undefined);
    };

    const handleChangeLineHaulModalError = (msg) => {
        setNotification({ text: msg, level: 'error' });
    };

    const handleOpenResetOrderModal = (row) => {
        handleMenuClose();
        setOrderModal((prevState) => ({
            ...prevState,
            resetOrderModalIsOpen: true,
            order: row,
        }));
        handleMenuClose();
    };

    const handleResetOrderModalClose = () => {
        setOrderModal((prevState) => ({
            ...prevState,
            resetOrderModalIsOpen: false,
            order: undefined,
        }));
    };

    const handleChangeOrderCarrierServiceModalSuccess = (msg) => {
        handleChangeOrderCarrierServiceModalClose();
        setNotification({ text: msg, level: 'success' });
        refetchOrders();
    };
    const handleResetOrderModalSuccess = (msg) => {
        handleResetOrderModalClose();
        setNotification({ text: msg, level: 'success' });
        refetchOrders();
    };

    const handleChangeOrderCarrierServiceModalError = (msg) => {
        setNotification({ text: msg, level: 'error' });
    };

    const handleResetOrderModalError = (msg) => {
        setNotification({ text: msg, level: 'error' });
    };

    const handleOpenChangeOrderLineHaulModal = async (row) => {
        handleMenuClose();
        const shipments = await fbmOrdersV2Api.getShipments(row.fbmOrderNum, fbmAccount.fbmAccountName);
        if (!shipments.status) {
            setNotification({ text: shipments.message, level: 'error' });
            return;
        }
        setOrderModal((prevState) => ({
            ...prevState,
            changeLineHaulModalIsOpen: true,
            order: row,
            shipments: shipments.data.rows,
        }));
    };

    const handleChangeOrderLineHaulModalClose = () => {
        setOrderModal((prevState) => ({
            ...prevState,
            changeLineHaulModalIsOpen: false,
            order: undefined,
        }));
    };

    const handleChangeOrderLineHaulModalSuccess = (msg) => {
        handleChangeOrderLineHaulModalClose();
        setNotification({ text: msg, level: 'success' });
        refetchOrders();
    };

    const handleChangeOrderLineHaulModalError = (msg) => {
        setNotification({ text: msg, level: 'error' });
    };

    return (
        <>
            <Notifications options={notification} />
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <div className="flex items-center w-full gap-2 bg-white rounded-lg">
                        <Tooltip title="Fuzzy search across all displayed columns" placement="right" arrow>
                            <IconButton size="small" className="text-gray-500 hover:text-gray-700">
                                <InfoOutlined fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <TextField
                            id="filled-search"
                            placeholder="Search Orders"
                            type="search"
                            variant="outlined"
                            fullWidth
                            size="small"
                            InputProps={{
                                notched: false,
                            }}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: 'white',
                                    height: '36px',
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#e5e7eb',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#9ca3af',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#6366f1',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    padding: '8px 14px',
                                },
                            }}
                            onChange={(e) => setSearch({ ...search, term: e.target.value })}
                            onKeyPress={handleKeyPress}
                        />
                    </div>
                </Grid>
                <Grid item xs={1}>
                    <Button variant="outlined" startIcon={<SearchOutlined />} onClick={handleSearch}>
                        Search
                    </Button>
                </Grid>
                <Grid item xs={4} md={2}>
                    {user.fbm && (
                        <FacilityPicker
                            defaultValue={fbmAccount.facilityIDs[0]}
                            whitelistedFacilities={fbmAccount.facilityIDs}
                            activeOnly
                            onChange={(facilityID, text, facility) => {
                                setFacilityID(facilityID);
                            }}
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={5} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {bulkActions.map((item, index) => (
                        <React.Fragment key={index}>{item.componentJSX()}</React.Fragment>
                    ))}
                </Grid>

                <Grid item xs={12}>
                    {bulkFailedOrders?.length > 0 && (
                        <Alert
                            severity="error"
                            className="mb-5"
                            action={
                                <Button color="inherit" size="small" onClick={handleClearFailures}>
                                    Clear
                                </Button>
                            }
                        >
                            <AlertTitle>Bulk Action Failed</AlertTitle>
                            <div className="max-h-[200px] overflow-y-auto pr-2">
                                <Grid container spacing={2}>
                                    {bulkFailedOrders.map((failure, index) => (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={bulkFailedOrders.length === 1 ? 12 : 6}
                                            md={bulkFailedOrders.length === 1 ? 8 : 4}
                                            key={failure.orderId || index}
                                        >
                                            <div className="p-3 bg-white/[0.09] rounded">
                                                <Typography variant="subtitle2" className="font-bold">
                                                    Order ID: {failure.orderId}
                                                </Typography>
                                                <Typography variant="body2" className="mt-1 text-red-500 break-words">
                                                    {failure.error}
                                                </Typography>
                                            </div>
                                        </Grid>
                                    ))}
                                </Grid>
                            </div>
                        </Alert>
                    )}
                    <ExpandableDataGrid
                        rows={orderData}
                        columns={columns}
                        childRows={shipmentsData}
                        childColumns={childColumns}
                        isLoading={ordersLoading}
                        isChildrenLoading={shipmentsLoading}
                        childGridTitle="Shipments"
                        onSelectedRowsChange={handleSelectedRowsChange}
                        selectedRow={selectedRow}
                        onSelectedRowChange={setSelectedRow}
                        onColumnHeaderClick={handleColumnHeaderClick}
                        onSortModelChange={handleSortModelChange}
                        onFilterModelChange={handleFilterModelChange}
                    />
                </Grid>
            </Grid>
            <CancelFBMShipmentModal
                isOpen={shipmentModal.cancelModalIsOpen}
                onClose={handleCancelShipmentModalClose}
                onSuccess={handleCancelShipmentModalSuccess}
                onError={handleCancelShipmentModalError}
                shipmentData={shipmentModal.shipment}
            />
            <ChangeFBMShipmentCarrierServiceModal
                isOpen={shipmentModal.changeCarrierServiceModalIsOpen}
                onClose={handleChangeShipmentCarrierServiceModalClose}
                onSuccess={handleChangeShipmentCarrierServiceModalSuccess}
                onError={handleChangeShipmentCarrierServiceModalError}
                shipmentData={shipmentModal.shipment}
            />
            <ChangeFBMOrderCarrierServiceModal
                isOpen={orderModal.changeCarrierServiceModalIsOpen}
                onClose={handleChangeOrderCarrierServiceModalClose}
                onSuccess={handleChangeOrderCarrierServiceModalSuccess}
                onError={handleChangeOrderCarrierServiceModalError}
                orderData={orderModal.order}
            />
            <ResetFBMShipmentStatusModal
                isOpen={shipmentModal.resetStatusModalIsOpen}
                onClose={handleResetStatusModalClose}
                onSuccess={handleResetStatusModalSuccess}
                onError={handleResetStatusModalError}
                shipmentData={shipmentModal.shipment}
            />
            <CancelFBMOrderModal
                isOpen={orderModal.cancelOrderModalIsOpen}
                onClose={handleCancelOrderModalClose}
                onSuccess={handleCancelOrderModalSuccess}
                onError={handleCancelOrderModalError}
                orderData={orderModal.order}
            />
            <ChangeFBMShipmentLineHaulModal
                isOpen={shipmentModal.changeLineHaulModalIsOpen}
                onClose={handleChangeLineHaulModalClose}
                onSuccess={handleChangeLineHaulModalSuccess}
                onError={handleChangeLineHaulModalError}
                shipmentData={shipmentModal.shipment}
            />
            <ResetFBMOrderStatusModal
                isOpen={orderModal.resetOrderModalIsOpen}
                onClose={handleResetOrderModalClose}
                onSuccess={handleResetOrderModalSuccess}
                onError={handleResetOrderModalError}
                orderData={orderModal.order}
            />
            <ChangeFBMOrderLineHaulModal
                isOpen={orderModal.changeLineHaulModalIsOpen}
                onClose={handleChangeOrderLineHaulModalClose}
                onSuccess={handleChangeOrderLineHaulModalSuccess}
                onError={handleChangeOrderLineHaulModalError}
                orderData={orderModal.order}
                shipments={orderModal.shipments}
            />
        </>
    );
}

export { ViewAllOrdersV2 };
